/**
 * Sonner shim.
 *
 * The `sonner` package's `<Toaster />` component was crashing under
 * React 19 with "Cannot read properties of null (reading 'useState')"
 * because it bundles its own React resolution path that doesn't match
 * the app's React copy. That crash bubbled up to the global
 * ErrorBoundary and prevented `useSubscription` / `usePlatformAccess`
 * from ever running — which is why entitled users (e.g. Kaiden) saw
 * the "Subscription Required" gate by mistake.
 *
 * We:
 *   1. Replace the visual `<Toaster />` from sonner with a no-op.
 *      The legacy `@/components/ui/toaster` rendered in App.tsx covers
 *      visual rendering via the `useToast` store.
 *   2. Re-export `toast` from a shim that bridges to the legacy
 *      `useToast` store, so the ~120 callers across the codebase keep
 *      showing toasts without any per-file changes.
 */
import { toast as legacyToast } from "@/hooks/use-toast";

const Toaster = (_props: Record<string, unknown>) => null;

type ToastInput =
  | string
  | {
      title?: React.ReactNode;
      description?: React.ReactNode;
      duration?: number;
    };

const normalize = (
  message: ToastInput,
  options?: { description?: React.ReactNode; duration?: number },
) => {
  if (typeof message === "string") {
    return {
      title: message,
      description: options?.description,
      duration: options?.duration,
    };
  }
  return {
    title: message.title,
    description: message.description ?? options?.description,
    duration: message.duration ?? options?.duration,
  };
};

type ToastFn = ((
  message: ToastInput,
  options?: { description?: React.ReactNode; duration?: number },
) => void) & {
  success: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  error: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  info: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  warning: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  message: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  loading: (message: ToastInput, options?: { description?: React.ReactNode }) => void;
  promise: <T>(
    promise: Promise<T>,
    msgs: { loading?: string; success?: string | ((d: T) => string); error?: string | ((e: unknown) => string) },
  ) => Promise<T>;
  dismiss: (id?: string | number) => void;
  custom: (message: ToastInput) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _legacyToast = legacyToast as unknown as (args: any) => unknown;

const baseToast = ((message: ToastInput, options?) => {
  _legacyToast(normalize(message, options));
}) as ToastFn;

baseToast.success = (m, o) => _legacyToast({ ...normalize(m, o) });
baseToast.error = (m, o) => _legacyToast({ ...normalize(m, o), variant: "destructive" });
baseToast.info = (m, o) => _legacyToast({ ...normalize(m, o) });
baseToast.warning = (m, o) => _legacyToast({ ...normalize(m, o) });
baseToast.message = (m, o) => _legacyToast({ ...normalize(m, o) });
baseToast.loading = (m, o) => _legacyToast({ ...normalize(m, o) });
baseToast.custom = (m) => _legacyToast(normalize(m));
baseToast.dismiss = () => {
  /* legacy store dismisses on its own timer */
};
baseToast.promise = async (p, msgs) => {
  if (msgs?.loading) _legacyToast({ title: msgs.loading });
  try {
    const result = await p;
    if (msgs?.success) {
      const text = typeof msgs.success === "function" ? msgs.success(result) : msgs.success;
      _legacyToast({ title: text });
    }
    return result;
  } catch (err) {
    if (msgs?.error) {
      const text = typeof msgs.error === "function" ? msgs.error(err) : msgs.error;
      _legacyToast({ title: text, variant: "destructive" });
    }
    throw err;
  }
};

export { Toaster };
export const toast = baseToast;
